import { PipelineId, PipelineStageId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { Checkbox } from "components/ui/checkbox";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { produce } from "immer";
import React from "react";
import { EditableWorkflow } from "./types";

interface PipelineStageChangedTriggerEditorProps {
    workflow: EditableWorkflow;
    onChange: (newWorkflow: EditableWorkflow) => void;
}

export const PipelineStageChangedTriggerEditor: React.FC<
    PipelineStageChangedTriggerEditorProps
> = props => {
    const { workflow, onChange } = props;
    const editable = !!workflow.regionVisibility?.editable;

    const trpcClient = trpc.useTRPC();
    const pipelinesQuery = useQuery(trpcClient.pipeline.list.queryOptions());
    const pipelines = pipelinesQuery.data ?? [];

    const configuration = workflow.trigger?.pipelineStageChanged;

    if (pipelinesQuery.isLoading) {
        return <Spinner />;
    }

    if (!configuration) {
        return null;
    }

    const selectedPipeline = pipelines.find(p => p.id === configuration.pipelineId);

    const handlePipelineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newWorkflow = produce(workflow, draft => {
            draft.trigger.pipelineStageChanged.pipelineId = e.target.value as PipelineId;
            draft.trigger.pipelineStageChanged.toStageIds = [];
        });

        onChange(newWorkflow);
    };

    const handleStageToggle = (stageId: PipelineStageId, checked: boolean) => {
        const newWorkflow = produce(workflow, draft => {
            const toStageIds = draft.trigger.pipelineStageChanged.toStageIds;
            draft.trigger.pipelineStageChanged.toStageIds = checked
                ? [...toStageIds, stageId]
                : toStageIds.filter((id: PipelineStageId) => id !== stageId);
        });

        onChange(newWorkflow);
    };

    return (
        <div className="space-y-2">
            <Select
                disabled={!editable}
                label="Pipeline"
                value={configuration.pipelineId}
                onChange={handlePipelineChange}>
                <option value="">Select a pipeline</option>
                {pipelines.map(pipeline => (
                    <option key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                    </option>
                ))}
            </Select>
            {selectedPipeline ? (
                <div>
                    <p>Trigger when the card enters any of these stages:</p>
                    {selectedPipeline.stages.map(stage => (
                        <Checkbox
                            key={stage.id}
                            disabled={!editable}
                            label={stage.name}
                            checked={configuration.toStageIds.includes(stage.id)}
                            onChange={e => handleStageToggle(stage.id, e.target.checked)}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
};
